import { useState, useEffect, useRef } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import {
  getSiteContacts,
  createSiteContact,
  SiteContact,
} from '../../lib/tbm';
import {
  fetchComposedApprovalLinePreview,
  type ApprovalLinePreviewData,
} from '../../lib/reports';
import type { ReportTemplate } from '@safetyhub/shared';
import { findEntryUserInputMeta } from './composer/improvementCandidates';
import { SiteContactFormModal, SiteContactFormValue } from '../sites/SiteContactFormModal';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG, type ModalLayer } from '../modalLayers';
import { ApprovalLineSection } from './sections/ApprovalLineSection';
import type { ApprovalLineItem } from './sections/approval-line';
import {
  approvalPrefixViolationMessage,
  approvalStepLabel,
  findApprovalPrefixViolation,
  findIdentityLostApprovalStep,
  identityLostApprovalStepMessage,
  isDelegationOwnedStep,
} from './sections/approval-line/approvalLineIdentity';
// 배럴(`./sections/approval-line`)이 아니라 **깊은 경로**로 가져간다.
// 배럴은 `ApprovalLineSectionImpl`까지 함께 끌어오는데, 이 화면은 그 섹션을 별도 모듈
// (`./sections/ApprovalLineSection`)로 주입받고 spec은 그 모듈을 `vi.mock`으로 스텁한다.
// 배럴 경유로 바꾸면 **mock을 우회해 실제 섹션이 모달의 모듈 그래프에 딸려 들어온다**
// (섹션이 부르는 API·훅까지 함께) — 순수 변환 하나를 쓰자고 치를 값이 아니다.
import { buildItemsFromPreviewBaseRows } from './sections/approval-line/previewRows';
import { useUser } from '../../stores/auth';
import { apiErrorMessage } from '../../lib/api';
import { getDelegationDefault, setDelegationDefault } from '../../utils/delegationDefaults';

export type { ApprovalLineItem };

export interface DelegationApprovalLineValue {
  // 추가 결재자 (안전관리자 제외, level 2+)
  additionalLines: Array<{
    /**
     * 멤버(조직 구성원) 기반 결재자 — 신원의 유일한 축(구 `approverProfileId`는 2026-09-16 E4에서 삭제).
     * 편집 모달에서 멤버를 고르면 이 값이 채워지므로(useApprovalLineEdit.handleAddMember), 서버 payload에
     * 실어야 결재 단계가 소실되지 않는다. 값이 없으면 키를 싣지 않는다(빈 문자열 아님, F-J).
     */
    memberId?: string;
    approvalLevel: number;
    label?: string;
    /**
     * 결재란 칸의 통지 채널 — **정본 축**(NotifyKind[], 2026-08-23 단일화). 빠뜨리면 서버가
     * 폴백(제한 없음)으로 떨어져 사용자의 채널 선택이 무시된다(F-J) — 왕복 보존이 계약이다.
     * 위임 **수신** 채널(이 모달의 `notifyChannels` state — 위임받는 담당자에게 링크를 보낼
     * 채널)과는 다른 축이므로 이름을 구분한다.
     */
    notifyVia?: ('sms' | 'email')[];
    /**
     * 담당자 미지정 칸(오프라인 수기) 마커. 편집 모달에서 '담당자 해제'한 칸이 여기로 온다.
     * 칸 추가와 담당자 지정은 별개 축이라 미지정은 정상 상태이며(결정 2026-08-13), 이 마커를
     * 빠뜨리면 서버가 "profile도 member도 없는 빈 행"으로 보고 칸을 통째로 잃는다.
     */
    assigneeUnset?: boolean;
  }>;
}

export interface ReportDelegationModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  siteId: string;
  /** 표시용 레이블 (예: "TBM 작성", "일일안전점검일지") */
  reportTypeLabel: string;
  /**
   * 결재라인 정책을 조회할 템플릿 — approvalLineMeta 기반 preview 조회 키(template.key)로 사용.
   * 없으면 정책 없음(빈 preview)으로 본다. (정책 분기·localStorage 저장 키의 축도 템플릿 key다 —
   * 키 형식은 `delegationDefaults`가 소유한다.)
   */
  template?: ReportTemplate;
  /**
   * 오버레이 층 — 층은 **여는 쪽이 정한다**(`modalLayers` 규약). 미지정 시 `MODAL_LAYER.sub`
   * (문서 유형 선택 `z-[70]` 위 — 종전 값). ⚠️ 이 안에서 여는 결재라인 편집(`editModalIsSub`)과
   * 「＋ 새 담당자」(`sub2`)는 **sub 전제의 층**을 쓰므로, 이 값을 올리면 그 둘도 함께 올려야 한다.
   */
  layer?: ModalLayer;
  onSelfSelected: () => void;
  onDelegateSelected: (params: {
    contact: SiteContact;
    notifyChannels: ('sms' | 'email')[];
    approvalLines?: DelegationApprovalLineValue;
    /**
     * 진입 전 사용자 입력(COMPOSED 위임 전용) — 위임자가 위임 시점에 입력한 AI 생성 지시문(trim,
     * 빈 값 생략). 템플릿 entryUserInput 켜짐 + userInput 메타 파트 매칭일 때만 입력란이 노출된다.
     * 결정: 2026-07-16-composer-ai-guidance-regenerate(2026-07-26 위임 흐름 개정 — 입력 주체=위임자).
     */
    entryGuidance?: string;
  }) => void | Promise<void>;
}

type Mode = 'self' | 'delegate';

/**
 * 결재라인 행 → 위임 additionalLines 공통 변환 — **서버 payload와 localStorage 복원 저장이
 * 이 함수 하나를 쓴다.** 두 곳이 따로 손으로 조립하면 한쪽에만 필드가 빠져도 조용히 갈라진다
 * (F-J 실사고: notifyChannels가 손나열에서 빠져 사용자의 채널 선택이 무시/유실됐다).
 *
 * - memberId: 결재자 신원의 유일한 축(구 프로필 축은 2026-09-16 E4에서 삭제) — 이걸 빠뜨리면
 *   서버가 결재 단계를 통째로 잃는다(silent partial save). 값이 없으면 키를 싣지 않는다(F-J).
 * - notifyVia: 결재란 칸의 통지 채널 — **정본 축**(2026-08-23 단일화. 구 객체 축
 *   notifyChannels는 저장·복원 경계에서 notifyVia로 흡수돼 여기 도달하지 않는다).
 *   빠뜨리면 채널 선택이 유실된다(F-J 실사고와 같은 계열) — 왕복 보존이 계약이다.
 *
 * 두 소비처가 갈리는 필드는 **호출부가 덧붙인다**: 서버 payload만 `assigneeUnset`(담당자
 * 미지정 칸 마커). 복원 저장분에는 싣지 않는다 — 복원 필터(delegationDefaults의
 * isRestorableLine)가 "지목할 사람이 없는 행"을 버리므로 그 칸은 복원되지 않고, 따라서
 * 미지정 칸이 다음 위임에서 지정 칸으로 되살아나는 경로도 없다.
 */
function toAdditionalLine(l: ApprovalLineItem) {
  return {
    ...(l.memberId ? { memberId: l.memberId } : {}),
    approvalLevel: l.approvalLevel,
    label: l.label,
    notifyVia: l.notifyVia,
  };
}

/**
 * 위임 담당자가 바뀐 preview가 도착했을 때의 결재라인 갱신 — **위임이 소유한 칸만 다시
 * 해소하고 나머지 행은 그대로 둔다**(F-49).
 *
 * 종전에는 행을 통째로 비워 섹션이 새 preview로 다시 세우게 했다. 그러면 섹션이 아는
 * 것(고정 결재란 + 지난 위임 복원분)만 되살아나고 **이번 모달에서 사용자가 추가한 칸이
 * 조용히 사라진다** — 「담당자를 김대표로 바꾸면 검증 란이 없어진다」(2026-09-13).
 * 담당자 교체로 달라지는 것은 체인 머리가 `delegatedTo`인 칸 하나뿐이라, 그 칸만 새 base
 * row로 갈아끼우는 것이 정확한 처방이다.
 *
 * 매칭 축은 **level이 아니라 체인 머리**다(`isDelegationOwnedStep`). 사용자가 칸을 더하거나
 * 지우면 level이 밀리므로 level로 짝지으면 엉뚱한 칸을 덮어쓴다. 양쪽(현재 행·새 base row)을
 * 같은 술어로 걸러 순서대로 짝짓고, 짝이 없는 행은 건드리지 않는다.
 *
 * 갈아끼우는 것은 **신원 축**(memberId·member·sourceType·_assignmentChain·_locked)뿐이다.
 * 칸의 위치(approvalLevel)·이름(label)·통지 채널(notifyVia)은 사용자 편집 축이라 보존한다 —
 * 여기서 base row 값으로 덮으면 위와 같은 계열의 조용한 편집 손실이 된다.
 */
function remergeDelegationOwnedRows(
  prev: ApprovalLineItem[],
  preview: ApprovalLinePreviewData,
): ApprovalLineItem[] {
  // 아직 행이 없으면 그대로 둔다 — 섹션의 초기화가 새 preview로 처음 세운다.
  if (prev.length === 0) return prev;
  const resolvedDelegationRows = buildItemsFromPreviewBaseRows(preview).filter(isDelegationOwnedStep);
  if (resolvedDelegationRows.length === 0) return prev;
  let nextIndex = 0;
  return prev.map((row) => {
    if (!isDelegationOwnedStep(row)) return row;
    const resolved = resolvedDelegationRows[nextIndex];
    if (!resolved) return row;
    nextIndex += 1;
    return {
      ...resolved,
      approvalLevel: row.approvalLevel,
      label: row.label,
      notifyVia: row.notifyVia,
    };
  });
}

export function ReportDelegationModal({
  isOpen,
  onClose,
  companyId,
  siteId,
  reportTypeLabel,
  template,
  layer = MODAL_LAYER.sub,
  onSelfSelected,
  onDelegateSelected,
}: ReportDelegationModalProps) {
  const currentUser = useUser();
  const [mode, setMode] = useState<Mode>('self');
  const [contacts, setContacts] = useState<SiteContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<SiteContact | null>(null);
  const [notifyChannels, setNotifyChannels] = useState<('sms' | 'email')[]>([]);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ApprovalLineSection(standalone)이 관리하는 전체 결재라인 (level 1+)
  const [approvalLineItems, setApprovalLineItems] = useState<ApprovalLineItem[]>([]);
  // localStorage default가 적용되었는지 추적 (contacts 로드 후 1회만 적용)
  const [defaultApplied, setDefaultApplied] = useState(false);
  /** 지난 위임 기본값 — 결재라인 복원은 ApprovalLineSection이 정책 대조 후 수행한다. */
  const [storedDefault, setStoredDefault] = useState<ReturnType<typeof getDelegationDefault>>(null);
  /** 현장 기본(고정 결재란) 서명 — 섹션이 알려주고, 위임 확정 시 그대로 저장한다. */
  const [baseSignature, setBaseSignature] = useState('');
  // 진입 전 사용자 입력(COMPOSED 위임 전용) — 위임 시점 위임자 지시문(선택).
  const [entryGuidanceValue, setEntryGuidanceValue] = useState('');

  // 진입 전 사용자 입력란 노출 판정 — 템플릿 전역 entryUserInput 게이트 + 트리에서 userInput 메타
  // 파트를 쓰는 sortableSelect 매칭(문구는 위젯 override → 파트 메타 fallback, rc 해소기 정본).
  // 타입 선택 모달의 직접 작성 패널과 동일 게이트/문구(2026-07-26 위임 흐름 — 입력 주체=위임자).
  const entryInput = template?.entryUserInput
    ? findEntryUserInputMeta(template.composerWidgetTree)
    : null;

  /**
   * 담당자 선택/추가 시 알림 채널 초기 기본값 — 담당자가 보유한 첫 연락처 채널 1개.
   * 발송 채널은 위임 시점 선택이 유일 정본이라(2026-07-26 템플릿 기본값 축 제거) 보고서 type과
   * 무관하게 동일하다. localStorage 직전 선택(getDelegationDefault)은 아래 복원 effect가 덮는다.
   */
  const defaultChannelsFor = (contact: SiteContact): ('sms' | 'email')[] => {
    const available: ('sms' | 'email')[] = [];
    if (contact.phone) available.push('sms');
    if (contact.email) available.push('email');
    if (available.length === 0) return [];
    return [available[0]];
  };

  /**
   * 이 모달 세션에서 담당자별로 **마지막으로 정해진 채널**(F-50, 2026-09-13 사용자 실측 「임경업은
   * 이메일로 선택돼 있었는데 김대표를 골랐다가 다시 임경업을 고르면 SMS로 돼 있다」).
   *
   * 종전에는 담당자를 고를 때마다 `defaultChannelsFor`(보유 첫 채널)로 되돌려, 지난 위임에서
   * 복원된 채널이든 사용자가 손으로 고른 채널이든 **다른 담당자를 거쳐 돌아오면 사라졌다.**
   * 담당자를 바꾸는 것은 그 사람의 채널 선택을 버리는 행위가 아니다.
   *
   * 출처 우선순위: ① 이 세션에서 그 담당자에게 정해진 값(복원·수동 토글) ② 지난 위임 기본값
   * (localStorage — 그 담당자일 때만, 보유 채널로 걸러) ③ 보유 첫 채널. 모달이 닫히면 비운다
   * (세션 밖 기억은 ②가 맡는다).
   */
  const channelsByContactRef = useRef<Map<string, ('sms' | 'email')[]>>(new Map());
  const rememberChannels = (contactId: string, channels: ('sms' | 'email')[]) => {
    channelsByContactRef.current.set(contactId, channels);
  };
  const channelsForContact = (contact: SiteContact): ('sms' | 'email')[] => {
    const remembered = channelsByContactRef.current.get(contact.id);
    if (remembered) return remembered;
    const stored = currentUser?.id ? getDelegationDefault(currentUser.id, siteId) : null;
    if (stored && stored.contactId === contact.id) {
      const valid = stored.notifyChannels.filter(
        (ch) => (ch === 'sms' && contact.phone) || (ch === 'email' && contact.email),
      );
      if (valid.length > 0) return valid;
    }
    return defaultChannelsFor(contact);
  };

  // 템플릿 approvalLineMeta 기반 결재라인 정책 여부.
  // null=로딩 중, true/false=확정. 로딩 중에는 섹션 보류(깜빡임 방지).
  const [serverHasApprovalLinePolicy, setServerHasApprovalLinePolicy] = useState<boolean | null>(null);
  // 템플릿 approvalLineMeta 기반 preview(baseRows/maxAdditionalLines).
  // ApprovalLineSection에 presetPreview로 주입.
  const [composedPreview, setComposedPreview] = useState<ApprovalLinePreviewData | null>(null);

  /**
   * 고른 위임 담당자의 Member id — 결재란의 `{type:'delegatedTo'}` 단계를 해소하는 축이다.
   *
   * 이걸 안 넘기던 동안 담당자를 골라도 1행(체인 `위임 담당자|작성자`)이 **작성자로 남았다**.
   * 저장하면 서버가 위임 담당자로 해소하므로 **미리보기만 실제와 달랐던 것**이고, 그건 이
   * 화면의 존재 이유를 놓치는 상태다. 서버 쿼리도 클라 함수 파라미터도 처음부터 있었는데
   * 넘기는 호출부가 없었다(2026-09-09).
   */
  const delegatedToMemberId = selectedContact?.memberId;
  /**
   * 마지막으로 **결재라인에 반영된** 위임 담당자.
   *
   * preview를 다시 받아도 섹션의 초기화는 「이미 행이 있으면 건너뛴다」라(`approvalLines.length
   * > 0` 가드) 새 해소 결과가 조용히 무시된다 — 담당자를 바꿔도 1행이 그대로였던 두 번째
   * 원인이다. 그래서 담당자가 바뀐 조회의 응답이 오면 **위임이 소유한 칸을 새 base row로
   * 갈아끼운다**(`remergeDelegationOwnedRows`) — 행이 아직 없을 때만 섹션이 세운다.
   *
   * ⚠️ 종전에는 여기서 행을 **통째로 비웠다**. 그러면 섹션이 아는 것(고정 결재란 + 지난 위임
   * 복원분)만 되살아나 **이번 모달에서 사용자가 추가한 칸이 사라진다**(2026-09-13 F-49:
   * 「담당자를 바꾸면 검증 란이 없어진다」). 담당자 교체가 바꾸는 것은 그 한 칸뿐이다.
   *
   * 갱신 시점을 응답 도착으로 잡은 것도 요점이다 — 담당자가 바뀌는 순간에 손대면 섹션이
   * **옛 preview**로 다시 세우고, 그 뒤 새 preview가 와도 가드에 막힌다(경쟁).
   */
  const appliedDelegateRef = useRef<string | null | undefined>(null);

  useEffect(() => {
    if (!isOpen || !siteId) return;
    let cancelled = false;
    // 정책은 템플릿 approvalLineMeta 기반 — template.key로 report-composer preview 조회(cascade 갈래 없음).
    const previewPromise = template?.key
      ? fetchComposedApprovalLinePreview(template.key, siteId, delegatedToMemberId)
      : Promise.resolve<ApprovalLinePreviewData>({
          baseRows: [],
          maxAdditionalLines: 0,
          hasApprovalLinePolicy: false,
          baseNonLockedCount: 0,
        });
    previewPromise
      .then((preview) => {
        if (cancelled) return;
        setServerHasApprovalLinePolicy(preview.hasApprovalLinePolicy);
        setComposedPreview(preview);
        if (appliedDelegateRef.current !== delegatedToMemberId) {
          appliedDelegateRef.current = delegatedToMemberId;
          setApprovalLineItems((prev) => remergeDelegationOwnedRows(prev, preview));
        }

      })
      .catch(() => {
        if (!cancelled) setServerHasApprovalLinePolicy(false);
      });
    return () => { cancelled = true; };
    // 담당자를 바꾸면 다시 해소해야 한다 — 이 의존성이 없으면 첫 조회 결과가 그대로 굳어
    // 담당자를 고른 뒤에도 1행이 작성자로 남는다(2026-09-09).
  }, [isOpen, siteId, template?.key, delegatedToMemberId]);

  const showApprovalLineSection = serverHasApprovalLinePolicy === true;

  useEffect(() => {
    if (!isOpen) {
      setMode('self');
      setSelectedContact(null);
      setNotifyChannels([]);
      setValidationError(null);
      setApprovalLineItems([]);
      appliedDelegateRef.current = null;
      // 담당자별 채널 기억은 이 모달 세션 안의 것이다(F-50) — 세션 밖은 localStorage 기본값이 맡는다.
      channelsByContactRef.current.clear();
      setDefaultApplied(false);
      setIsSubmitting(false);
      setComposedPreview(null);
      setServerHasApprovalLinePolicy(null);
      setEntryGuidanceValue('');
      // 위임 기본값은 현장·보고서 타입별이라 한 번의 위임 흐름에만 속한다. 남겨두면 다음에
      // **다른 현장**으로 열었을 때 이전 현장의 결재라인이 복원되거나(restoreFrom), 이전 정책
      // 서명이 저장돼 복원 판정이 어긋난다(baseSignature).
      setStoredDefault(null);
      setBaseSignature('');
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !companyId || !siteId) return;
    setIsLoadingContacts(true);
    getSiteContacts(companyId, siteId)
      .then(setContacts)
      .catch(() => setContacts([]))
      .finally(() => setIsLoadingContacts(false));
  }, [isOpen, companyId, siteId]);

  /**
   * 위임 기본값을 **모달 열자마자** 읽는다(localStorage는 동기라 contacts를 기다릴 이유가 없다).
   *
   * 결재라인 복원은 ApprovalLineSection의 초기화 effect가 preview를 받은 직후 수행하는데,
   * 이 값을 contacts 응답 뒤에 세팅하면 둘이 경합해 preview가 먼저 오는 순간 복원 기회를
   * 놓친다(담당자 복원과 달리 재시도 지점이 없다). 담당자 매칭만 contacts를 기다린다.
   */
  useEffect(() => {
    if (!isOpen || !currentUser?.id) return;
    setStoredDefault(getDelegationDefault(currentUser.id, siteId));
  }, [isOpen, currentUser?.id, siteId]);

  // contacts 로드 후 localStorage default 복원 (1회)
  useEffect(() => {
    if (!isOpen || contacts.length === 0 || !currentUser?.id || defaultApplied) return;
    setDefaultApplied(true);
    const stored = getDelegationDefault(currentUser.id, siteId);
    if (!stored) return;
    const found = contacts.find((c) => c.id === stored.contactId);
    if (!found) return; // contact 삭제/비활성화 — silent drop
    // 저장된 채널 중 contact가 실제로 보유한 채널만 적용
    const validChannels = stored.notifyChannels.filter(
      (ch) => (ch === 'sms' && found.phone) || (ch === 'email' && found.email),
    );
    // 직접 상태 설정 (selectedContact useEffect 덮어쓰기 방지)
    setSelectedContact(found);
    const restoredChannels = validChannels.length > 0 ? validChannels : [];
    setNotifyChannels(restoredChannels);
    // 복원된 채널도 그 담당자의 값으로 기억한다(F-50) — 다른 담당자를 골랐다 돌아와도 유지.
    rememberChannels(found.id, restoredChannels);
  }, [isOpen, contacts, currentUser?.id, siteId, defaultApplied]);


  if (!isOpen) return null;

  const departmentSuggestions = Array.from(
    new Set(contacts.map((c) => c.department).filter((d): d is string => !!d?.trim())),
  );

  const handleAddContact = async (value: SiteContactFormValue) => {
    const created = await createSiteContact(companyId, siteId, {
      name: value.name,
      phone: value.phone,
      department: value.department,
      position: value.position,
      email: value.email,
    });
    setContacts((prev) => [...prev, created]);
    setSelectedContact(created);
    // 새 담당자 추가 시 채널 기본값 — 보유 첫 채널(defaultChannelsFor).
    setNotifyChannels(defaultChannelsFor(created));
    setIsAddingContact(false);
  };

  const toggleChannel = (ch: 'sms' | 'email') => {
    setNotifyChannels((prev) => {
      const next = prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch];
      // 수동 선택은 그 담당자의 값으로 기억한다(F-50) — 다른 담당자를 거쳐 돌아와도 유지.
      if (selectedContact) rememberChannels(selectedContact.id, next);
      return next;
    });
    setValidationError(null);
  };

  const handleConfirm = async () => {
    if (mode === 'self') {
      onSelfSelected();
      return;
    }

    if (!selectedContact) {
      setValidationError('담당자를 선택해주세요.');
      return;
    }

    const hasPhone = !!selectedContact.phone;
    const hasEmail = !!selectedContact.email;
    if (!hasPhone && !hasEmail) {
      setValidationError('선택한 담당자에게 연락처(전화번호 또는 이메일)가 등록되어 있지 않습니다. 담당자 정보를 수정해주세요.');
      return;
    }

    if (notifyChannels.length === 0) {
      setValidationError('알림을 보낼 채널을 최소 1개 선택해주세요.');
      return;
    }

    // ── 결재라인 전송 전 가드 — 편집 모달(ApprovalLineSectionImpl.handleSaveEdit)과 **같은 규칙** ──
    // 이 모달은 섹션의 저장 경로를 거치지 않고 approvalLineItems를 직접 payload로 만들기 때문에,
    // 편집 모달에 있는 두 가드를 타지 않았다. 그래서 여기서 같은 공용 판정을 다시 적용한다
    // (복제 금지 — approvalLineIdentity의 함수와 문구를 그대로 쓴다).
    // 서버도 같은 규칙으로 거부하지만, 위임은 실패해도 되돌릴 게 많으므로 보내기 전에 막는다.
    const identityLostIndex = findIdentityLostApprovalStep(approvalLineItems);
    if (identityLostIndex >= 0) {
      setValidationError(
        identityLostApprovalStepMessage(approvalStepLabel(approvalLineItems, identityLostIndex)),
      );
      return;
    }
    const prefixViolationIndex = findApprovalPrefixViolation(approvalLineItems);
    if (prefixViolationIndex >= 0) {
      setValidationError(
        approvalPrefixViolationMessage(approvalStepLabel(approvalLineItems, prefixViolationIndex)),
      );
      return;
    }

    // level 2+ 항목만 additionalLines로 변환하여 서버 형식과 호환.
    // memberId를 싣는다 — 결재자 신원의 유일한 축이라(값 없으면 키째 생략, F-J) 이걸 빠뜨리면
    // 서버가 결재 단계를 통째로 잃는다(silent partial save).
    // locked(admin 고정) 단계는 빼고 보낸다 — "추가 결재란"이 아니라 정책이 박아둔 칸이다.
    // 서버는 템플릿 메타에서 그 단계를 다시 만들므로 보낼 필요가 없고, 보내면 생성 경로의
    // 한도 검사가 그것까지 세어 부당한 400을 낸다.
    // 실제로 일일안전점검일지는 level 2+ locked 1칸이 있어 한도 2가 1로 줄어 보였다(2026-08-12).
    // ⚠️ 여기 남는 비-locked에는 **템플릿이 정의한 편집 허용 칸**도 섞여 있다 — 서버의
    // 생성 경로 한도 검사는 그래서 `양식 단독 비-locked 칸 수 + maxAdditionalLines`를
    // 상한으로 쓴다(2026-08-13 새 모델). 이 필터를 바꾸면 그 상한도 같이 봐야 한다.
    const additionalLines = approvalLineItems
      .filter((l) => l.approvalLevel >= 2 && l._locked !== true)
      .map((l) => ({
        // 필드 조립은 복원 저장분과 공통(toAdditionalLine) — 두 벌 손나열 금지(F-J 재발 방지).
        ...toAdditionalLine(l),
        // 담당자를 뗀 칸도 **칸으로는 남겨야** 한다 — 이 마커가 빠지면 서버가 빈 행으로 보고
        // 칸을 통째로 잃는다(편집 모달의 '담당자 해제'가 위임 경로에서만 소실되던 원인).
        // 지정된 행에는 필드를 싣지 않는다(undefined 유지) — 기존 흐름 무변경.
        // 이 마커만 payload 전용이다(복원 저장분에는 없다 — toAdditionalLine doc 참조).
        ...(l._assigneeUnset === true ? { assigneeUnset: true as const } : {}),
      }));

    if (currentUser?.id) {
      setDelegationDefault(currentUser.id, siteId, {
        contactId: selectedContact.id,
        notifyChannels,
        // 복원용도 payload와 **같은 공통 변환**(toAdditionalLine)을 쓴다 — memberId·채널
        // 선택(notifyVia)까지 함께 실려야 다음 위임에서 그 사람·그 채널이 복원된다(F-J).
        // locked(정책 고정) 단계를 제외하는 축도 payload와 같다 — 저장해 두면 다음 복원에서
        // 정책이 만든 칸이 '추가 결재자'로 한 번 더 붙는다. 담당자 미지정 칸의 assigneeUnset
        // 마커는 payload 전용이라 여기엔 싣지 않는다(toAdditionalLine doc 참조).
        additionalLines: approvalLineItems
          .filter((l) => l.approvalLevel >= 2 && l._locked !== true)
          .map(toAdditionalLine),
        // 저장 시점 정책 서명 — 다음 복원에서 현장 기본이 그 사이 바뀌었는지 대조한다.
        baseSignature,
      });
    }

    setIsSubmitting(true);
    setValidationError(null);
    try {
      await Promise.resolve(
        onDelegateSelected({
          contact: selectedContact,
          notifyChannels,
          approvalLines: showApprovalLineSection
            ? { additionalLines }
            : undefined,
          // 진입 전 사용자 입력 — trim 후 빈 값이면 생략(미제공=무지시, 서버 캐시 우선 사전 생성).
          ...(entryInput && entryGuidanceValue.trim()
            ? { entryGuidance: entryGuidanceValue.trim() }
            : {}),
        }),
      );
    } catch (err) {
      // ⚠️ **이 모달이 위임 실패의 유일한 표시처**다(2026-09-10) — 부모(문서 유형 선택)는 이제
      // 잡지 않고 던지기만 한다. 사용자가 보고 있는 자리가 여기이므로, 서버가 「무엇을 해야
      // 하는지」 말해 준 문구를 여기서 버리면 그 안내는 어디에도 닿지 않는다.
      //
      // 종전엔 `(err as Error)?.message`였다 — axios 기본 문구라 위임 게이트 400에
      // **"Request failed with status code 400"** 이 그대로 떴다(사용자 실측). `apiErrorMessage`는
      // 서버가 준 문구만 돌려주고 네트워크·알 수 없는 예외에는 null을 줘 아래 일반 문구로
      // 떨어진다 — 즉 raw가 사용자에게 보일 길이 없다(그 헬퍼 JSDoc이 이 규약의 정본).
      setValidationError(apiErrorMessage(err) ?? '위임 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* 이 모달은 문서 유형 선택(root) 위에서 열리는 sub 층이다 — 층은 여는 쪽이 `layer`로
          주입한다(기본 sub). 값을 직접 박으면 층이 흩어져 갈라지므로(2026-08-11 z층 사고) 상수를
          참조한다 — 이 층이 올라가면 이 안에서 여는 결재라인 편집(sub2)·담당자 추가(sub2)도 함께
          올려야 한다. 제출(위임 전송) 중에는 닫기 수단을 전부 막는다(종전 오버레이 가드의 승계). */}
      <Modal
        open
        onClose={onClose}
        title={reportTypeLabel}
        size="md"
        overlayZClassName={layer}
        overlayBgClassName={SUB_MODAL_OVERLAY_BG}
        closeOnOverlay={!isSubmitting}
        closeOnEsc={!isSubmitting}
        showCloseButton={!isSubmitting}
        closeLocked={isSubmitting}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting} className="flex-1">
              취소
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  전송 중...
                </span>
              ) : (
                mode === 'self' ? '작성하기' : '위임하기'
              )}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-500 mb-5">작성 방식을 선택해주세요.</p>

        {/* 모드 선택 */}
        <div className="flex gap-3 mb-5">
          <button
            type="button"
            onClick={() => setMode('self')}
            className={`flex-1 py-3 px-4 rounded-card border-2 text-sm font-medium transition-colors ${
              mode === 'self'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-line text-ink-600 hover:border-ink-300'
            }`}
          >
            본인 작성
          </button>
          <button
            type="button"
            onClick={() => setMode('delegate')}
            className={`flex-1 py-3 px-4 rounded-card border-2 text-sm font-medium transition-colors ${
              mode === 'delegate'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-line text-ink-600 hover:border-ink-300'
            }`}
          >
            담당자에게 위임
          </button>
        </div>

        {/* 위임 모드 UI */}
        {mode === 'delegate' && (
          <div className="space-y-4 mb-4">
            {/* 담당자 선택 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">담당자 선택</label>
              {isLoadingContacts ? (
                <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ink-400" />
                  담당자 목록 불러오는 중...
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {contacts.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => {
                        if (selectedContact?.id === contact.id) {
                          setSelectedContact(null);
                          setNotifyChannels([]);
                        } else {
                          setSelectedContact(contact);
                          // 직접 선택 시 채널 — 이 세션에서 그 담당자에게 정해진 값이
                          // 있으면 그것, 없으면 지난 위임 기본값, 없으면 보유 첫 채널
                          // (F-50 — 담당자를 오갔다고 채널 선택을 버리지 않는다).
                          setNotifyChannels(channelsForContact(contact));
                        }
                        setValidationError(null);
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-card-sm border transition-colors ${
                        selectedContact?.id === contact.id
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-line bg-white text-ink-700 hover:border-ink-300'
                      }`}
                    >
                      <span className="font-medium">{contact.name}</span>
                      {contact.position && <span className="text-ink-400 text-xs">({contact.position})</span>}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setIsAddingContact(true)}
                    className="inline-flex items-center px-3 py-2 text-sm text-emerald-600 border border-dashed border-emerald-300 rounded-card-sm hover:bg-emerald-50"
                  >
                    + 새 담당자
                  </button>
                </div>
              )}
            </div>

            {/* 알림 채널 */}
            {selectedContact && (
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">알림 발송 채널</label>
                <div className="flex gap-3">
                  {selectedContact.phone ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifyChannels.includes('sms')}
                        onChange={() => toggleChannel('sms')}
                        className="rounded border-ink-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">SMS ({selectedContact.phone})</span>
                    </label>
                  ) : (
                    <span className="text-sm text-ink-400 line-through">SMS (미등록)</span>
                  )}
                  {selectedContact.email ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifyChannels.includes('email')}
                        onChange={() => toggleChannel('email')}
                        className="rounded border-ink-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">이메일 ({selectedContact.email})</span>
                    </label>
                  ) : (
                    <span className="text-sm text-ink-400 line-through">이메일 (미등록)</span>
                  )}
                </div>
                {!selectedContact.phone && !selectedContact.email && (
                  <p className="mt-1.5 text-xs text-red-500">
                    이 담당자에게는 등록된 연락처가 없습니다. 담당자를 다시 선택하거나 새로 추가해주세요.
                  </p>
                )}
              </div>
            )}

            {/* 진입 전 사용자 입력(COMPOSED 위임 전용) — 위임자가 위임 시점에 입력하는 AI 생성
                지시문(선택). 문구는 위젯 override → 파트 메타 fallback(rc 해소기). */}
            {entryInput && (
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  {entryInput.label} <span className="text-xs text-ink-400">(선택)</span>
                </label>
                <textarea
                  value={entryGuidanceValue}
                  onChange={(e) => setEntryGuidanceValue(e.target.value)}
                  rows={3}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50"
                  placeholder={entryInput.placeholder}
                />
                <p className="mt-1 text-xs text-ink-400">
                  입력한 내용이 위임 시 AI 생성에 반영됩니다. 같은 입력이면 다시 생성하지 않습니다.
                </p>
              </div>
            )}

            {/* 결재라인 편집 (type별 분기) — ApprovalLineSection standalone 모드 재사용 */}
            {showApprovalLineSection && (
              <ApprovalLineSection
                mode="standalone"
                // 이 섹션은 위임 모달(z-[80]) 안에 있다 — 편집 모달을 sub 층으로 올리지
                // 않으면 z-[70]이라 위임 모달 뒤로 숨는다(2026-08-11).
                editModalIsSub
                /* 위임 흐름 편집 — 체인 머리가 `delegatedTo`인 칸은 이 탭이 값을 소유한다.
                   담당자를 아직 안 골랐으면 그 칸 이름이 폴백(작성자)이라 **저장 결과와
                   다른 사람**을 가리킨다(2026-09-09 dev). 그래서 미정임을 그대로 말한다. */
                delegationSlot={selectedContact ? 'assigned' : 'pending'}
                // 지난 위임의 추가 결재자 복원 + 정책 서명 회신(저장용).
                restoreFrom={storedDefault}
                onBaseSignature={setBaseSignature}
                companyId={companyId}
                siteId={siteId}
                // 템플릿 approvalLineMeta preview를 주입 — 섹션은 이 값으로만 정책을 안다.
                presetPreview={composedPreview ?? undefined}
                approvalLines={approvalLineItems}
                onApprovalLinesChange={setApprovalLineItems}
                editable={true}
                maxApprovers={3}
                requiredFirstStep={{
                  label: '안전관리자',
                  locked: true,
                }}
              />
            )}

            {/* 검증 에러 */}
            {validationError && (
              <p className="text-sm text-red-600">{validationError}</p>
            )}
          </div>
        )}
      </Modal>

      {/* '＋ 새 담당자'는 **이 위임 모달(sub) 위에서** 열린다 — 기본값(root z-[70])으로 두면
          위임 모달 뒤로 숨어 아무 반응이 없는 것처럼 보인다. 층은 여는 쪽이 정한다. */}
      <SiteContactFormModal
        open={isAddingContact}
        zIndexClass={MODAL_LAYER.sub2}
        departmentSuggestions={departmentSuggestions}
        onSubmit={handleAddContact}
        onCancel={() => setIsAddingContact(false)}
      />
    </>
  );
}
